import {
  Avatar,
  Card,
  Empty,
  notification,
  Typography,
  Input,
  Select,
  Button,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  DownOutlined,
  MoreOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  BankOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { translation } from "../../../i18n/translationConstant";
import LinkedDelinkedAccountResponse, {
  LinkedDelinkedAccountObject,
} from "../../../Models/Response/Account/LinkedDelinkedAccounts";
import {
  delinkAccounts,
  delinkAccountVerification,
  getLinkedAccountsOfUser,
} from "../../../Services/AccountsServices/AccountService";
import { authType } from "../../../Utils/Constants/authType";
import { Routes } from "../../../Utils/Constants/routes";

import EMPTY_STATE from "../../../Assets/empty_state.png";

import "./index.css";
import ConsentStatusApprovalComponent from "../../Consent/Components/StatusApproval";
import { useFingerprintValidation } from "../../Authentication/FingerprintValidationContext";

const { Option } = Select;

const Accounts: React.FC = () => {
  const { t } = useTranslation();
  const { push } = useHistory();

  const [linkedAccounts, setLinkedAccounts] = useState(
    new Array<LinkedDelinkedAccountObject>()
  );

  const [isOTPModalVisible, setIsOTPModalVisible] = useState(false);
  const [otpUniqueID, setOTPUniqueId] = useState("");
  const [isAuthTypeOTPModalVisible, setIsAuthTypeOTPModalVisible] =
    useState(false);
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [radioSelect, setRadioSelect] = useState(0);

  const [selectedAccount, setSelectedAccount] = useState({
    fipHandle: "",
    accountRefNumber: "",
    accountType: "",
    fiType: "",
    linkRefNumber: "",
    logoUrl: "",
    maskedAccNumber: "",
    fipName: "",
    txnId: "",
  });

  const [phoneNumber, setPhoneNumber] = useState("");

  const [searchText, setSearchText] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("all");

  const { checkFingerprint } = useFingerprintValidation();

  useEffect(() => {
    getLinkedAccounts();
    checkFingerprint();
  }, [checkFingerprint]);

  const getLinkedAccounts = () => {
    getLinkedAccountsOfUser()
      .then((res) => res.data)
      .then((res: LinkedDelinkedAccountResponse) => {
        setLinkedAccounts(res);
      });
  };

  const closeModal = () => {
    if (radioSelect === 0) {
      setIsPinModalVisible(false);
    } else {
      setIsOTPModalVisible(false);
    }
  };

  const onDelinkAccount = (otp: string) => {
    if (radioSelect === 0) {
      delinkAccountVerification({
        FipId: selectedAccount.fipHandle,
        txnId: selectedAccount.txnId,
        linkRefNumber: selectedAccount.linkRefNumber,
        authType: authType.PIN,
        authValue: otp,
        otpUniqueID,
      }).then(() => {
        notification.success({
          message: t(translation.lblAccountUnLinkedSuccess),
        });
        push(Routes.account);
        getLinkedAccounts();
        setIsOTPModalVisible(false);
        setIsPinModalVisible(false);
      });
    } else {
      delinkAccountVerification({
        FipId: selectedAccount.fipHandle,
        txnId: selectedAccount.txnId,
        linkRefNumber: selectedAccount.linkRefNumber,
        authType: authType.OTP,
        authValue: otp,
        otpUniqueID,
      }).then(() => {
        notification.success({
          message: t(translation.lblAccountUnLinkedSuccess),
        });
        push(Routes.account);
        getLinkedAccounts();
        setIsPinModalVisible(false);
        setIsOTPModalVisible(false);
      });
    }
  };

  const delinkAccount = (account: LinkedDelinkedAccountObject) => {
    setRadioSelect(1);

    delinkAccounts({
      FipId: account.fipHandle,
      txnId: account.txnId,
      linkRefNumber: account.linkRefNumber,
      maskedAccNumber: account.maskedAccNumber,
    })
      .then((res) => res.data)
      .then((res) => {
        setPhoneNumber(res.phoneNumber);
        setOTPUniqueId(res.otpUniqueID);
        setIsOTPModalVisible(true);
        setIsPinModalVisible(false);
        setIsAuthTypeOTPModalVisible(false);
      });
  };

  const radioSelectEvent = (e: number) => setRadioSelect(e);

  const onSelectionModal = () => {
    setIsAuthTypeOTPModalVisible(false);

    if (radioSelect === 0) {
      setIsPinModalVisible(true);
    } else {
      delinkAccounts({
        FipId: selectedAccount.fipHandle,
        txnId: selectedAccount.txnId,
        linkRefNumber: selectedAccount.linkRefNumber,
        maskedAccNumber: selectedAccount.maskedAccNumber,
      })
        .then((res) => res.data)
        .then((res) => {
          setIsOTPModalVisible(true);
          setOTPUniqueId(res.otpUniqueID);
        });
    }
  };

  const filteredAccounts = linkedAccounts.filter(
    (account: LinkedDelinkedAccountObject) => {
      const search = searchText.toLowerCase();

      const matchesSearch =
        !search ||
        account.fipName.toLowerCase().includes(search) ||
        account.maskedAccNumber.toLowerCase().includes(search);

      const matchesType =
        accountTypeFilter === "all" ||
        account.accountType.toLowerCase() ===
          accountTypeFilter.toLowerCase();

      return matchesSearch && matchesType;
    }
  );

  const accountMenu = (account: LinkedDelinkedAccountObject) => ({
    items: [
      {
        key: "unlink",
        label: t(translation.lblUnLinkAccounts),
        onClick: () => {
          setSelectedAccount(account);
          delinkAccount(account);
        },
      },
    ],
  });

  return (
    <div className="accountsPageRoot">
      <ConsentStatusApprovalComponent
        typeOfModal={"Unlink Account"}
        isAuthTypeSelectionModalVisible={isAuthTypeOTPModalVisible}
        onCloseAuthTypeSelectionModal={() =>
          setIsAuthTypeOTPModalVisible(false)
        }
        onAuthTypeModalSubmit={onSelectionModal}
        onAuthTypeRadioSelectionChange={radioSelectEvent}
        selectedAuthType={radioSelect}
        verifyOTPAuthType={onDelinkAccount}
        isAuthTypeOTPModalVisible={isOTPModalVisible}
        onCloseOTPModal={closeModal}
        pinModalVisible={isPinModalVisible}
        mobileNumber={phoneNumber}
      />

      {/* Page heading */}
      <div className="accountsPageHeader">
        <div>
          <Typography.Title level={2} className="accountsPageTitle">
            {t(translation.lblLinkedAccounts)}
          </Typography.Title>

          <Typography.Text className="accountsPageSubtitle">
            {t(translation.lblManageLinkedAccounts)}
          </Typography.Text>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="newAccountButton"
          onClick={() => push(Routes.linkAccounts)}
        >
          {t(translation.lblLinkNewAccount)}
        </Button>
      </div>

      {/* Search / filters */}
      <div className="accountFilters">
        <Input
          prefix={<SearchOutlined />}
          placeholder={t(translation.lblSearchInstitution)}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="accountSearch"
          allowClear
        />

        <Select
          value={accountTypeFilter}
          onChange={(value) => setAccountTypeFilter(value)}
          className="accountFilterSelect"
          suffixIcon={<DownOutlined />}
        >
          <Option value="all">
            {t(translation.lblAllTypes)}
          </Option>
          <Option value="savings">
            {t(translation.lblSavings)}
          </Option>
          <Option value="current account">
            {t(translation.lblCurrentAccount)}
          </Option>
          <Option value="deposit">
            {t(translation.lblDeposit)}
          </Option>
          <Option value="insurance">
            {t(translation.lblInsurance)}
          </Option>
        </Select>
      </div>

      {/* FI-P information */}
      <div className="fipInformationBanner">
        <div className="fipInfoLeft">
          <InfoCircleOutlined />
          <span>
            {t(translation.lblFindAndLinkAccounts)}
          </span>
        </div>

        <Button
          type="link"
          className="viewFipsButton"
          onClick={() => push(Routes.linkAccounts)}
        >
          {t(translation.lblViewAllFips)} <ArrowRightOutlined />
        </Button>
      </div>

      {/* Account count */}
      <div className="linkedAccountsCount">
        {filteredAccounts.length}{" "}
        {t(translation.lblLinkedAccountsCount)}
      </div>

      {/* Account cards */}
      <div className="accountsRoot">
        {filteredAccounts.length ? (
          filteredAccounts.map(
            (account: LinkedDelinkedAccountObject, index: number) => (
              <Card className="linkedAccountCard" key={index}>
                <div className="accountCardContent">
                  <div className="accountLogoWrapper">
                    {account.logoUrl ? (
                      <Avatar
                        className="accountLogo"
                        src={account.logoUrl}
                      />
                    ) : (
                      <div className="defaultBankLogo">
                        <BankOutlined />
                      </div>
                    )}
                  </div>

                  <div className="accountCardMain">
                    <div className="accountCardTop">
                      <div className="accountInstitution">
                        <Typography.Text className="accountCardFIPText">
                          {account.fipName}
                        </Typography.Text>

                        <Typography.Text className="accountCardNumber">
                          {account.maskedAccNumber}
                        </Typography.Text>
                      </div>

                      <div className="accountCardActions">
                        <span className="linkedStatus">
                          <span className="statusDot">✓</span>
                          {t(translation.lblLinked)}
                        </span>

                        <Dropdown
                          menu={accountMenu(account)}
                          trigger={["click"]}
                        >
                          <Button
                            type="text"
                            className="moreButton"
                            icon={<MoreOutlined />}
                            aria-label={t(
                              translation.lblAccountActions
                            )}
                          />
                        </Dropdown>
                      </div>
                    </div>

                    <div className="accountTags">
                      <span className="accountTag">
                        {account.accountType}
                      </span>

                      <span className="accountTag">
                        {account.fiType}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            )
          )
        ) : (
          <Empty
            className="accountsEmptyState"
            image={EMPTY_STATE}
            description={
              <>
                <Typography.Text className="consentErrorMsg">
                  Oops!
                </Typography.Text>
                <br />
                <Typography.Text className="consentErrorSubText">
                  {t(translation.lblNoLinkedAccounts)}
                </Typography.Text>
              </>
            }
          />
        )}
      </div>
    </div>
  );
};

export default Accounts;